import {computed, watch, ref} from 'vue';
import {useRoute, useRouter} from "vue-router";
import {useCookie, useState, useNuxtApp} from '#app';
import {useGoogleRecaptcha} from "~/composables/useGoogleRecaptcha";
import {endpoints} from "~/network/endpoints.js";
import {useHttpRequest} from "~/composables/useHttpRequest";
export function useAuthService() {
  const { $cometChatSdk, $cometChatUiKit } = useNuxtApp(); // Use injected CometChat
  const cometChatSdk = ref($cometChatSdk);
  const cometChatUiKit = ref($cometChatUiKit);

  const cometUser = useState('cometUser', () => null);
  const unreadCount = useState('unreadCount', () => 0);
  const router = useRouter();
  const route = useRoute();

  const { setAlertMessage } = useAlertService();

  const {executeRecaptcha} = useGoogleRecaptcha();
  // Using useCookie to store tokens securely
  const userCookie = useCookie('user', {maxAge: 60 * 60 * 24 * 7});
  const accessTokenCookie = useCookie('accessToken', {maxAge: 60 * 60 * 24 * 7});
  const impersonatingUserCookie = useCookie('impersonatingUser', {maxAge: 60 * 60 * 24 * 7});
  const impersonatingAccessTokenCookie = useCookie('impersonatingAccessToken', {maxAge: 60 * 60 * 24 * 7});

  const _user = useState('user', () => userCookie.value || null);
  const user = computed(() => _user.value ?? {});
  const accessToken = useState('accessToken', () => accessTokenCookie.value || null);
  const impersonatingUser = useState('impersonatingUser', () => impersonatingUserCookie.value || null);
  const impersonatingAccessToken = useState('impersonatingAccessToken', () => impersonatingAccessTokenCookie.value || null);

  // Forgot Password Action
  const {
    item: forgotPasswordItem,
    createItem: forgotPasswordAction,
    formPending: forgotPasswordLoading,
    formErrors: forgotPasswordErrors,
      hasFormErrors : hasForgotPasswordErrors
  } = useCrudApi('api/v2/auth/forgot-password');

  // Login Action
  const {
    item: loginItem,
    createItem: loginAction,
    formPending: loginLoading,
    formErrors: loginErrors,
      hasFormErrors : hasLoginErrors
  } = useCrudApi('api/v2/auth/login');

  // Register Action
  const {
    item: registerItem,
    createItem: registerAction,
    formPending: registerLoading,
    formErrors: registerErrors,
      hasFormErrors : hasRegisterErrors
  } = useCrudApi('api/v2/auth/me');
  const {item: meItem, getItem: getMe, itemPending: meLoading, itemErrors: meErrors} = useCrudApi('api/v2/auth/me');
  const {
    item: impersonateItem,
    getItem: impersonate,
    itemPending: impersonateLoading,
    itemErrors: impersonateErrors
  } = useCrudApi('api/v2/users');


// Get SSR cookie value
  const useBetaDashboardCookie = useCookie('useBetaDashboard', {
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax',
    path: '/',
  });

  const useBetaDashboard = useState('useBetaDashboard', () => {
    if(!user.value.betaDashboardAvailable && !impersonatingAccessToken.value) {
        return false; // If user doesn't have access, default to false
    }
    // Initial value on SSR
    if (process.server) {
      const cookieHeader = useRequestHeaders().cookie ?? '';
      const match = cookieHeader.match(/useBetaDashboard=(true|false)/);
      return match?.[1] === 'true';
    }

    // Fallback default on client if hydration fails
    return false;
  });

// Only set it once — use SSR cookie if available
  if (process.client) {
    watch(useBetaDashboard, (val) => {
      useBetaDashboardCookie.value = val ? 'true' : 'false';
    });

    // Prevent client from overriding SSR value unless cookie exists
    const cookieVal = useBetaDashboardCookie.value;
    if (cookieVal === 'true' || cookieVal === 'false') {
      useBetaDashboard.value = cookieVal === 'true';
    }
  }

  const init = async () => {
    _user.value = userCookie.value || null;
    accessToken.value = accessTokenCookie.value || null;
    impersonatingUser.value = impersonatingUserCookie.value || null;
    impersonatingAccessToken.value = impersonatingAccessTokenCookie.value || null;
    updateAuthHeaders();
    if(isAuthenticated.value) {
      await updateLocalUser();
    }
    setCrudOnUnauthenticated(() => {
      logout();
    });
  };
  const getAccessToken = () => {
    if (impersonatingAccessToken.value) {
      return impersonatingAccessToken.value;
    } else if (accessToken.value) {
      return accessToken.value;
    }
    return null;
  }

  const onNewMessage = async (message) => {
    setAlertMessage({
      message: `New message from ${message.sender.name}`,
      color: 'info',
      location: 'bottom',
      icon : 'mdi-chat',
      onClick : () => {
        router.push({path: '/chat'});
      }
    });
    await updateUnreadCount();
  }

  const updateUnreadCount = async () => {
    if (cometUser.value) {
      if (!cometChatSdk.value) return;
      cometChatSdk.value.getUnreadMessageCountForAllUsers().then((res) => {
        // total all unread messages
        unreadCount.value = Object.values(res).reduce((acc, val) => acc + val, 0);
      });
    }
  }

  const loadingChat = ref(true);
  const setupCometUser = async () => {
    if (process.client && _user.value && _user.value.cometChatAuthToken) {

      loadingChat.value = true;
      if (!cometChatSdk.value || !cometChatUiKit.value) return;
      cometUser.value = await cometChatUiKit.value.getLoggedinUser();
      if (
          cometUser.value === null || //Not logged in
          cometUser.value.uid !== _user.value.id || // Logged in as wrong user
          cometUser.value.authToken !== _user.value.cometChatAuthToken // Auth token changed
          ) {
        if(cometUser.value !== null) {
          await cometChatUiKit.value.logout();
        }
        try {
          cometUser.value = await cometChatUiKit.value.loginWithAuthToken(_user.value.cometChatAuthToken);
        } catch (e) {
          console.error("Error logging in with CometChat Auth Token", e);
          loadingChat.value = false;
          return;
        }
      }
      updateUnreadCount();
      cometChatSdk.value.removeUserListener('MESSAGE_LISTENER_KEY');
      cometChatSdk.value.addMessageListener(
          "MESSAGE_LISTENER_KEY",
          new cometChatSdk.value.MessageListener({
            onTextMessageReceived: (textMessage) => {
              onNewMessage(textMessage);
            },
            onMediaMessageReceived: (mediaMessage) => {
              onNewMessage(mediaMessage);
            },
            onCustomMessageReceived: (customMessage) => {
              onNewMessage(customMessage);
            },
            onMessagesDelivered: (messageReceipt) => {
            },
            onMessagesRead: (messageReceipt) => {
            },
            onTypingStarted: (typingIndicator) => {
            },
            onTypingEnded: (typingIndicator) => {
            },
            onMessagesDeleted: (message) => {
            },
            onMessageEdited: (message) => {
            },
            onInteractiveMessageReceived: (message) => {
            },
            onInteractionGoalCompleted: (message) => {
            },
            onTransientMessageReceived: (message) => {
            },
            onMessageReactionAdded: (reaction) => {
            },
            onMessageReactionRemoved: (reaction) => {
            },
          })
      );
      loadingChat.value = false;
    }
  }

  const updateLocalUser = async () => {
    await getMe("");
    _user.value = meItem.value;
    await setupCometUser();
  }

  const updateAuthHeaders = () => {
    if (impersonatingAccessToken.value) {
      setCrudHeadersGenerator(() => ({
        Authorization: `Bearer ${impersonatingAccessToken.value}`
      }));
    } else if (accessToken.value) {
      setCrudHeadersGenerator(() => ({
        Authorization: `Bearer ${accessToken.value}`
      }));
    } else {
      setCrudHeadersGenerator(() => ({}));
    }
  }

  const login = async (credentials) => {
    await loginAction(credentials);
    const result = loginItem.value;
    if (result) {
      accessToken.value = result.accessToken;
      _user.value = result;
      // Store in cookies
      accessTokenCookie.value = result.accessToken;
      userCookie.value = JSON.stringify(result);
      if(process.client) {
        await setupCometUser();
      }
    }
    updateAuthHeaders();
  };

  const validateShortLivedToken = async (token) => {
    const { httpRequest } = useHttpRequest(); // Access the HTTP request utility
    let result = null;
    try {
      // Perform the network request to validate the token
      result = await httpRequest(
          endpoints.auth.validateShortLivedToken(),
          {"token": token},
      );

      if (result && result.user) {
        const user = result.user;
        accessToken.value = user.accessToken;
        _user.value = user;
        accessTokenCookie.value = user.accessToken;
        userCookie.value = JSON.stringify(user);
        updateAuthHeaders();
      }
      return result;
    } catch (error) {
      console.error("Error validating short-lived token:", error);
      throw error;
    }
  };

  const impersonateUser = async (userId) => {
    await impersonate(userId + "/impersonate");
    const result = impersonateItem.value;
    impersonateLoading.value = true;
    if (result) {
      impersonatingAccessToken.value = result;
      impersonatingAccessTokenCookie.value = result;
      updateAuthHeaders();
      await updateLocalUser();
      await router.replace({path:route.path, force : true});
    }
    impersonateLoading.value = false;
  };

  const stopImpersonating = async () => {
    impersonatingAccessToken.value = null;
    impersonatingUser.value = null;
    updateAuthHeaders();
    await updateLocalUser();
    await router.replace({path :route.path, force : true});
  };
  const forgotPassword = async (data) => {
    const {token} = await executeRecaptcha("forgotPassword");
    data.RecaptchaResponse = token;
    await forgotPasswordAction(data);
  };
  const register = async (credentials) => {
    const {token} = await executeRecaptcha("register");
    credentials.RecaptchaResponse = token;
    await registerAction(credentials);
  };

  const logout = async () => {
    cometUser.value = null;
    unreadCount.value = 0;
    _user.value = null;
    accessToken.value = null;
    impersonatingUser.value = null;
    impersonatingAccessToken.value = null;

    // Clear cookies
    userCookie.value = null;
    accessTokenCookie.value = null;
    impersonatingUserCookie.value = null;
    impersonatingAccessTokenCookie.value = null;
    updateAuthHeaders();

  };

  watch(_user, (newVal) => {
    if (newVal) {
      userCookie.value = JSON.stringify(newVal);
    } else {
      userCookie.value = null;
    }
  });

  watch(accessToken, (newVal) => {
    if (newVal) {
      accessTokenCookie.value = newVal;
    } else {
      accessTokenCookie.value = null;
    }
  });

  watch(impersonatingUser, (newVal) => {
    if (newVal) {
      impersonatingUserCookie.value = JSON.stringify(newVal);
    } else {
      impersonatingUserCookie.value = null;
    }
  });

  watch(impersonatingAccessToken, (newVal) => {
    if (newVal) {
      impersonatingAccessTokenCookie.value = newVal;
    } else {
      impersonatingAccessTokenCookie.value = null;
    }
  });

  const isAuthenticated = computed(() => _user.value !== null && accessToken.value !== null);

  const isSuperUser = computed(() => isAuthenticated.value && _user.value && _user.value.superAdmin);

  const isAdminOfClub = (clubId) => _user.value && _user.value.adminFor && _user.value.adminFor.indexOf(clubId) !== -1;

  const isMemberOfClub = (clubId) => _user.value && _user.value.memberOf && _user.value.memberOf.indexOf(clubId) !== -1;

  const isImpersonating = computed(() => impersonatingAccessToken.value !== null);

  /// USER VERIFICATION SHIT

  const userVerificationToken = ref({});
  const userVerificationError = ref({});
  const userVerificationSaveError = ref({});
  const userVerificationLoading = ref(false);
  const hasUserVerificationErrors = computed(() => userVerificationError.value && Object.keys(userVerificationError.value).length > 0);

  const getVerificationToken = async (token) => {
    const {httpRequest} = useHttpRequest();
    userVerificationLoading.value = true;
    userVerificationError.value = {};
    try {
      let res = await httpRequest(endpoints.auth.validateToken(token), {});
      userVerificationToken.value = res;
    } catch (e) {
      userVerificationError.value = e;
    } finally {
      userVerificationLoading.value = false;
    }
  };

  const acceptVerificationToken = async (token, data) => {
    const {httpRequest} = useHttpRequest();
    userVerificationLoading.value = true;
    userVerificationSaveError.value = {};
    try {
      let res = await httpRequest(endpoints.auth.acceptInvite(token, data), data);
      accessToken.value = res.accessToken;
      _user.value = res;
      // Store in cookies
      accessTokenCookie.value = res.accessToken;
      userCookie.value = JSON.stringify(res);
      updateAuthHeaders();
      await updateLocalUser();

    } catch (e) {
      userVerificationSaveError.value = e;
    } finally {
      userVerificationLoading.value = false;
    }
  };

  const declineVerificationToken = async (token) => {
    const {httpRequest} = useHttpRequest();
    userVerificationLoading.value = true;
    userVerificationSaveError.value = {};
    try {
      let res = await httpRequest(endpoints.auth.rejectInvite(token), {});
      userVerificationToken.value = res;
    } catch (e) {
      userVerificationSaveError.value = e;
    } finally {
      userVerificationLoading.value = false;
    }
  };

  return {
    user,
    updateLocalUser,
    useBetaDashboard,
    accessToken,
    impersonatingUser,
    impersonatingAccessToken,
    getAccessToken,
    isAuthenticated,
    isSuperUser,
    isAdminOfClub,
    isMemberOfClub,
    login,
    loginLoading,
    loginErrors,
    hasLoginErrors,
    register,
    registerLoading,
    registerErrors,
    hasRegisterErrors,
    registerItem,
    logout,
    init,
    impersonateUser,
    stopImpersonating,
    isImpersonating,
    impersonateItem,
    impersonateLoading,
    impersonateErrors,
    userVerificationToken,
    userVerificationError,
    userVerificationSaveError,
    userVerificationLoading,
    hasUserVerificationErrors,
    validateShortLivedToken,
    getVerificationToken,
    acceptVerificationToken,
    declineVerificationToken,
    forgotPasswordItem,
    forgotPassword,
    forgotPasswordLoading,
    forgotPasswordErrors,
    hasForgotPasswordErrors,
    unreadCount,
    setupCometUser,
    cometUser,
    loadingChat
  };
}
